/**
 * Active NWS alerts and conditions along the planned line.
 *
 * Separate from Events: that scan is scheduled disruptions from local
 * reporting. This one is what the weather service says is in force now.
 */
import React from 'react';
import { Box, Typography } from '@mui/material';
import { IntelSection } from './intel';
import { RouteWeatherReport } from '../useRouteWeather';
import {
  DockAction,
  EmptyState,
  ErrorLine,
  Loading,
  Provenance,
  TabHeader,
  WARN,
} from './DockPrimitives';

export interface WeatherTabProps {
  section: IntelSection<RouteWeatherReport>;
  hasRoute: boolean;
  onFocusAlert?: (latitude: number, longitude: number) => void;
}

const WeatherTab: React.FC<WeatherTabProps> = ({ section, hasRoute, onFocusAlert }) => {
  const report = section.data;
  const alerts = report?.alerts || [];
  const conditions = report?.conditions;
  const search = () => {
    if (section.enabled) section.refresh?.();
    else section.setEnabled?.(true);
  };
  const canSearch = hasRoute && (!!section.setEnabled || !!section.refresh);

  return (
    <Box>
      <TabHeader
        title="Weather"
        hint={alerts.length ? `${alerts.length} alert${alerts.length === 1 ? '' : 's'}` : undefined}
        actions={canSearch ? (
          <DockAction onClick={search} disabled={section.loading}>
            {report ? 'Scan again' : 'Scan weather'}
          </DockAction>
        ) : undefined}
      />

      {section.loading && <Loading label="Reading National Weather Service…" />}
      {section.error && <ErrorLine>{section.error}</ErrorLine>}

      {!report && !section.loading && (
        <EmptyState>
          {hasRoute
            ? 'Active NWS alerts and conditions along this route. Results stay here once scanned.'
            : 'Plan a route to scan weather along it.'}
        </EmptyState>
      )}

      {conditions && (
        <Typography variant="caption" sx={{ display: 'block', mb: 0.75, fontSize: '0.66rem' }}>
          {conditions.shortForecast}
          {conditions.temperature != null ? ` · ${conditions.temperature}°${conditions.temperatureUnit || 'F'}` : ''}
          {conditions.windSpeed ? ` · wind ${conditions.windSpeed}` : ''}
        </Typography>
      )}

      {report && alerts.length === 0 && !section.loading && (
        <EmptyState>No active weather alerts on this line.</EmptyState>
      )}

      {alerts.map((alert) => (
        <Box
          key={alert.id}
          role={onFocusAlert && alert.latitude != null && alert.longitude != null ? 'button' : undefined}
          tabIndex={onFocusAlert && alert.latitude != null ? 0 : -1}
          onClick={() => {
            if (alert.latitude != null && alert.longitude != null) {
              onFocusAlert?.(alert.latitude, alert.longitude);
            }
          }}
          sx={{
            py: 0.45,
            cursor: onFocusAlert && alert.latitude != null ? 'pointer' : 'default',
            '&:hover': onFocusAlert && alert.latitude != null ? { textDecoration: 'underline' } : undefined,
          }}
        >
          <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', color: WARN }}>
            {alert.event || 'Weather alert'}
          </Typography>
          <Typography variant="caption" sx={{ display: 'block', fontSize: '0.62rem', color: 'text.secondary' }}>
            {alert.headline || alert.area_description || alert.severity}
          </Typography>
        </Box>
      ))}

      <Provenance section={section} onRefresh={canSearch ? search : undefined} />
    </Box>
  );
};

export default WeatherTab;
